
// src/pages/Reports.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';
import { collection, onSnapshot, orderBy, query, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import {
  ref,
  getDownloadURL,
  deleteObject,
  getBlob,
  uploadBytes,
  listAll, // 👈 discovery fallback
  getStorage
} from 'firebase/storage';

import {
  AppBar,
  Toolbar,
  Container,
  Paper,
  Grid,
  Typography,
  TextField,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  IconButton,
  Button,
  Tooltip,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  LinearProgress,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Switch,
  FormControlLabel,
  Badge,
} from '@mui/material';

import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import DeselectIcon from '@mui/icons-material/DisabledByDefault';
import ArchiveIcon from '@mui/icons-material/Archive';
import FolderIcon from '@mui/icons-material/Folder';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

// ---------- Utils generali ----------
const storage = getStorage(); // ensure storage instance

const isHttpUrl = (s) => typeof s === 'string' && /^https?:\/\//i.test(s);
const isGsUrl  = (s) => typeof s === 'string' && /^gs:\/\//i.test(s);

// Trigger <a download> con filename sensato
function triggerAnchorDownload(blob, fileNameFallback, pathHint) {
  const suggested =
    (fileNameFallback || '').toString().split('/').pop() ||
    (pathHint || '').toString().split('/').pop() ||
    'download';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = suggested;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 0);
  return true;
}

// XHR fallback (alcuni CORS strani)
function xhrBlob(url) {
  return new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.responseType = 'blob';
      xhr.onload = function () {
        if (this.status >= 200 && this.status < 300) resolve(this.response);
        else reject(Object.assign(new Error(`HTTP ${this.status}`), { status: this.status }));
      };
      xhr.onerror = function () { reject(new Error('XHR network error')); };
      xhr.send();
    } catch (e) {
      reject(e);
    }
  });
}

async function fetchBlob(url) {
  try {
    const resp = await fetch(url, { method: 'GET', mode: 'cors', credentials: 'omit' });
    if (!resp.ok) {
      let txt = '';
      try { txt = await resp.text(); } catch {}
      const err = new Error(`HTTP ${resp.status}`);
      err.status = resp.status;
      if (txt) err.body = txt.slice(0, 200);
      throw err;
    }
    return await resp.blob();
  } catch (e1) {
    try {
      return await xhrBlob(url);
    } catch (e2) {
      const err = new Error(e1?.message || e2?.message || 'fetch/XHR failed');
      err.cause = { fetchErr: e1?.message, xhrErr: e2?.message };
      throw err;
    }
  }
}

// --- Prefissi cartelle anno + sottocartelle note ---
const YEAR_PREFIXES = [
  '01-Primo','02-Secondo','03-Terzo','04-Quarto','05-Quinto','06-Sesto','07-Settimo',
];

const KNOWN_SUBFOLDERS = [
  '', // direttamente nella cartella anno
  'Settimanali', 'Mensili', 'Statistiche',
  'weekly', 'monthly', 'stats',
  'reports', 'Reports',
  'Uploads', 'uploads',
  'Privacy', 'privacy'
];

// --- Varianti nome file ---
function makeNameVariants(originalName) {
  const base = (originalName || '').split('/').pop() || '';
  if (!base) return [];

  const parts = base.split('.');
  const ext   = parts.length > 1 ? parts.pop() : 'pdf';
  const name  = parts.join('.');

  const exts = Array.from(new Set([ext, ext.toLowerCase(), ext.toUpperCase(), 'pdf', 'PDF']));

  const withoutDashboard = name.replace(/__Dashboard__/gi, '__');
  const collapseUnderscore = (s) => s.replace(/_+/g, '_');
  const normalizedSpaces   = name.replace(/\s+/g, ' ');
  const dashTightened      = normalizedSpaces.replace(/\s*-\s*/g, '-');
  const dashSpaced         = normalizedSpaces.replace(/\s*-\s*/g, ' - ');

  const variantsCore = Array.from(new Set([
    name,
    withoutDashboard,
    collapseUnderscore(name),
    collapseUnderscore(withoutDashboard),
    dashTightened.replace(/\s+/g, '_'),
    dashSpaced.replace(/\s+/g, '_'),
    dashTightened,
    dashSpaced,
  ])).filter(Boolean);

  const full = [];
  for (const core of variantsCore) {
    for (const e of exts) {
      full.push(`${core}.${e}`);
    }
  }

  const set = new Set([`${name}.${ext}`, ...full]);
  return Array.from(set);
}

const normalizeForMatch = (s) =>
  (s || '').toLowerCase().replace(/[\s_-]+/g, '').replace(/\.pdf$/i, '');

// Tenta un singolo path: getBlob -> fallback getDownloadURL+fetch
async function tryDownloadFromPath(storageInstance, path, fileNameFallback) {
  if (!path) throw Object.assign(new Error('empty-path'), { code: 'empty-path' });

  if (isHttpUrl(path)) {
    const blob = await fetchBlob(path);
    triggerAnchorDownload(blob, fileNameFallback, path);
    return true;
  }

  if (isGsUrl(path)) {
    const url = await getDownloadURL(ref(storageInstance, path));
    const blob = await fetchBlob(url);
    triggerAnchorDownload(blob, fileNameFallback, path);
    return true;
  }

  const r = ref(storageInstance, path);
  try {
    const blob = await getBlob(r);
    triggerAnchorDownload(blob, fileNameFallback, path);
    return true;
  } catch {
    const url = await getDownloadURL(r); // se non esiste: object-not-found
    const blob = await fetchBlob(url);
    triggerAnchorDownload(blob, fileNameFallback, path);
    return true;
  }
}

// Discovery finale: lista cartelle note e cerca match per nome (robusto)
async function discoverPathByListing(storageInstance, originalName) {
  const variants = makeNameVariants(originalName);
  const normalizedTargets = variants.map(normalizeForMatch);
  const prefixesToScan = [
    '', // root
    ...YEAR_PREFIXES,
    // annoxsottocartella
    ...YEAR_PREFIXES.flatMap((yr) => KNOWN_SUBFOLDERS.filter(Boolean).map((sf) => `${yr}/${sf}`)),
    // solo sottocartelle alla root
    ...KNOWN_SUBFOLDERS.filter(Boolean),
  ];

  // Helper che scorre listAll (prefisso singolo)
  const scanPrefix = async (prefix) => {
    const folderRef = ref(storageInstance, prefix);
    const listing = await listAll(folderRef); // listAll gestisce internamente la paginazione
    // 1) match diretto sugli items del prefisso
    for (const it of listing.items) {
      const candidateName = it.name; // solo basename
      const normalizedCandidate = normalizeForMatch(candidateName);
      if (normalizedTargets.includes(normalizedCandidate)) {
        return it.fullPath; // match trovato
      }
    }
    // 2) opzionale: scendi di un livello nelle sottocartelle (non ricorsivo infinito)
    for (const sub of listing.prefixes) {
      const subListing = await listAll(sub);
      for (const it of subListing.items) {
        const normalizedCandidate = normalizeForMatch(it.name);
        if (normalizedTargets.includes(normalizedCandidate)) {
          return it.fullPath;
        }
      }
    }
    return null;
  };

  // Scansiona prefissi (limito per non stressare la quota)
  for (const p of prefixesToScan) {
    try {
      const found = await scanPrefix(p);
      if (found) return found;
    } catch {
      // ignoriamo errori singoli di listing su prefissi inesistenti/privati
    }
  }
  return null;
}

// Costruisce l’elenco di path da provare (candidati diretti + varianti)
function buildCandidatePaths(row, activeYearFilter) {
  const baseName = (row.fileName || '').split('/').pop();
  const cands = new Set();

  if (row.downloadURL && isHttpUrl(row.downloadURL)) cands.add(row.downloadURL);
  if (row.storagePath) cands.add(row.storagePath);
  if (row.fileName)    cands.add(row.fileName);

  if (row.yearFolder && baseName) {
    cands.add(`${row.yearFolder}/${baseName}`);
    for (const sub of KNOWN_SUBFOLDERS) {
      if (!sub) continue;
      cands.add(`${row.yearFolder}/${sub}/${baseName}`);
    }
  }

  if (activeYearFilter && activeYearFilter !== 'all' && baseName) {
    cands.add(`${activeYearFilter}/${baseName}`);
    for (const sub of KNOWN_SUBFOLDERS) {
      if (!sub) continue;
      cands.add(`${activeYearFilter}/${sub}/${baseName}`);
    }
  }

  if (baseName) {
    for (const yr of YEAR_PREFIXES) {
      cands.add(`${yr}/${baseName}`);
      for (const sub of KNOWN_SUBFOLDERS) {
        if (!sub) continue;
        cands.add(`${yr}/${sub}/${baseName}`);
      }
    }
  }

  const variants = makeNameVariants(baseName || row.fileName || '');
  const extra = new Set();

  for (const v of variants) extra.add(v);

  if (row.yearFolder) {
    for (const v of variants) extra.add(`${row.yearFolder}/${v}`);
    for (const sub of KNOWN_SUBFOLDERS) {
      if (!sub) continue;
      for (const v of variants) extra.add(`${row.yearFolder}/${sub}/${v}`);
    }
  }

  if (activeYearFilter && activeYearFilter !== 'all') {
    for (const v of variants) extra.add(`${activeYearFilter}/${v}`);
    for (const sub of KNOWN_SUBFOLDERS) {
      if (!sub) continue;
      for (const v of variants) extra.add(`${activeYearFilter}/${sub}/${v}`);
    }
  }

  for (const yr of YEAR_PREFIXES) {
    for (const v of variants) extra.add(`${yr}/${v}`);
    for (const sub of KNOWN_SUBFOLDERS) {
      if (!sub) continue;
      for (const v of variants) extra.add(`${yr}/${sub}/${v}`);
    }
  }

  const merged = Array.from(new Set([...cands, ...extra])).filter(Boolean);
  return merged.slice(0, 160); // limite prudenziale
}

// ---------- Helpers UI ----------
const fmtBytes = (bytes) => {
  if (bytes == null) return '-';
  if (bytes === 0) return '0 B';
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024)) || 0;
  const v = bytes / Math.pow(1024, i);
  return `${(Number.isFinite(v) ? v : 0).toFixed(1)} ${sizes[i] || 'B'}`;
};

const extOf = (name = '') => (name.toLowerCase().split('.').pop() || 'other');

const extColor = (ext) => {
  if (ext === 'pdf') return 'error';
  if (['xlsx', 'xls', 'csv'].includes(ext)) return 'success';
  return 'default';
};

// --- DATE HELPERS per "Data incontro" (preferisce Firestore -> fallback nome file) ---
function formatAsItDate(d) {
  try {
    const dd = `${d.getDate()}`.padStart(2, '0');
    const mm = `${d.getMonth() + 1}`.padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  } catch {
    return '';
  }
}

// prova a leggere meetingDate dal record (Date | Timestamp | string YYYY-MM-DD)
// se assente, prova dal nome file (supporto a 4 separatori: _, -, /, .)
function readRowMeetingDate(row) {
  try {
    // 1) meetingDate da Firestore
    const m = row?.meetingDate;
    if (m) {
      // Firestore Timestamp?
      if (typeof m?.toDate === 'function') {
        const d = m.toDate();
        if (d instanceof Date && !isNaN(d)) return { display: formatAsItDate(d), sortKey: `${d.getFullYear()}${(d.getMonth()+1+'').padStart(2,'0')}${(d.getDate()+'').padStart(2,'0')}` };
      }
      // JS Date
      if (m instanceof Date && !isNaN(m)) {
        return { display: formatAsItDate(m), sortKey: `${m.getFullYear()}${(m.getMonth()+1+'').padStart(2,'0')}${(m.getDate()+'').padStart(2,'0')}` };
      }
      // stringa tipo "YYYY-MM-DD"
      if (typeof m === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(m)) {
        const d = new Date(`${m}T00:00:00`);
        if (!isNaN(d)) return { display: formatAsItDate(d), sortKey: `${m.replace(/-/g,'')}` };
      }
    }

    // 2) fallback da fileName
    const name = row?.fileName || '';
    // accetta 09_11_2025 | 09-11-2025 | 09/11/2025 | 09.11.2025
    const m1 = name.match(/(\b\d{2})[._/-](\d{2})[._/-](\d{4})\b/);
    if (m1) {
      const dd = m1[1], mm = m1[2], yyyy = m1[3];
      const sortKey = `${yyyy}${mm}${dd}`;
      return { display: `${dd}/${mm}/${yyyy}`, sortKey };
    }

    return { display: '', sortKey: '' };
  } catch {
    return { display: '', sortKey: '' };
  }
}

// Filtri anno
const YEAR_FILTERS = [
  { value: 'all', label: 'Tutti gli anni' },
  { value: '01-Primo',   label: 'Primo anno' },
  { value: '02-Secondo', label: 'Secondo anno' },
  { value: '03-Terzo',   label: 'Terzo anno' },
  { value: '04-Quarto',  label: 'Quarto anno' },
  { value: '05-Quinto',  label: 'Quinto anno' },
  { value: '06-Sesto',   label: 'Sesto anno' },
  { value: '07-Settimo', label: 'Settimo anno' },
];

const normalizeKind = (rawKind) => {
  const k = (rawKind || '').toString().toLowerCase();
  if (k === 'weekly' || k === 'settimanale') return 'weekly';
  if (k === 'monthly' || k === 'mensile') return 'monthly';
  if (k === 'stats' || k === 'statistiche' || k === 'statistics') return 'stats';
  if (k === 'privacy' || k === 'consenso' || k === 'gdpr') return 'privacy';
  return k;
};

// 🔹 Mappa categorie → sottocartelle rilevanti
const CAT_FOLDERS = {
  weekly: ['Settimanali', 'weekly'],
  monthly: ['Mensili', 'monthly'],
  stats: ['Statistiche', 'stats'],
  privacy: ['Privacy', 'privacy'],
};

// ---------- Componente ----------
export default function Reports() {
  const navigate = useNavigate();

  // Dialog conferme
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('Conferma operazione');
  const [confirmMessage, setConfirmMessage] = useState('Sei sicuro di voler procedere?');
  const [confirmProceedLabel, setConfirmProceedLabel] = useState('Conferma');
  const [confirmCancelLabel, setConfirmCancelLabel] = useState('Annulla');
  const confirmResolverRef = useRef(null);

  const askConfirm = ({ title = 'Conferma operazione', message = 'Sei sicuro di voler procedere?', proceedLabel = 'Conferma', cancelLabel = 'Annulla' } = {}) => {
    setConfirmTitle(title);
    setConfirmMessage(message);
    setConfirmProceedLabel(proceedLabel);
    setConfirmCancelLabel(cancelLabel);
    setConfirmOpen(true);
    return new Promise((resolve) => { confirmResolverRef.current = resolve; });
  };
  const handleConfirmProceed = () => { setConfirmOpen(false); if (confirmResolverRef.current) confirmResolverRef.current(true); };
  const handleConfirmCancel  = () => { setConfirmOpen(false); if (confirmResolverRef.current) confirmResolverRef.current(false); };

  // Stato
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState(Number(localStorage.getItem('reports_active_tab') || 0));
  const [search, setSearch] = useState(localStorage.getItem('reports_search_term') || '');
  const [sortBy, setSortBy] = useState(localStorage.getItem('reports_sort_key') || 'date');
  const [order, setOrder] = useState(localStorage.getItem('reports_sort_order') || 'desc');
  const [selected, setSelected] = useState(new Set());
  const [snack, setSnack] = useState({ open: false, type: 'info', msg: '' });
  const [zipping, setZipping] = useState(false);
  const [zipProgress, setZipProgress] = useState(0);
  const [yearFilter, setYearFilter] = useState('all');
  const [showFolders, setShowFolders] = useState(localStorage.getItem('reports_show_folders') !== 'false');
  const [showFiles, setShowFiles] = useState(localStorage.getItem('reports_show_files') !== 'false');

  const isTrash = tab === 4;
  const currentCategory = useMemo(() => {
    if (tab === 0) return 'weekly';
    if (tab === 1) return 'monthly';
    if (tab === 2) return 'stats';
    if (tab === 3) return 'privacy';
    return 'weekly';
  }, [tab]);

  const notify = (type, msg) => setSnack({ open: true, type, msg });
  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  // Snapshot Firestore
  useEffect(() => {
    const qy = query(collection(db, 'reports'), orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(
      qy,
      (snap) => { setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); },
      (err) => { console.error('onSnapshot error', err); notify('error', 'Errore nel recupero dei report.'); }
    );
    return () => unsub();
  }, []);

  const recordInCurrentCategory = (r) => {
    const raw = r.bucket || r.kind;
    return normalizeKind(raw) === currentCategory;
  };

  const folderCounts = useMemo(() => {
    if (isTrash) return {};
    const counts = {};
    const inCat = items.filter((r) => recordInCurrentCategory(r) && !r.trashed);
    YEAR_FILTERS.filter(f => f.value !== 'all').forEach(f => {
      counts[f.value] = inCat.filter((it) => {
        const path = (it.storagePath || it.fileName || '').toLowerCase();
        return path.startsWith(f.value.toLowerCase() + '/');
      }).length;
    });
    return counts;
  }, [items, currentCategory, isTrash]);

  const handleTabChange = (_e, v) => {
    setTab(v);
    localStorage.setItem('reports_active_tab', String(v));
    clearSelection();
  };
  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearch(term);
    localStorage.setItem('reports_search_term', term);
  };

  const headerSortProps = (column) => ({
    active: sortBy === column,
    direction: sortBy === column ? (order === 'asc' ? 'asc' : 'desc') : 'asc',
    onClick: () => sortAndPersist(column, order),
  });
  const sortAndPersist = (by, currentOrder) => {
    if (sortBy === by) {
      const next = currentOrder === 'asc' ? 'desc' : 'asc';
      setOrder(next);
      localStorage.setItem('reports_sort_order', next);
    } else {
      setSortBy(by);
      localStorage.setItem('reports_sort_key', by);
    }
  };
  const toggleOrder = () => sortAndPersist(sortBy, order);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    let data = items.filter((r) => {
      const inBucket = recordInCurrentCategory(r);
      const trashed = !!r.trashed;
      return isTrash ? trashed : inBucket && !trashed;
    });

    if (yearFilter !== 'all') {
      data = data.filter((it) => {
        const path = ((it.storagePath || it.fileName || '') + '').toLowerCase();
        return path.startsWith(yearFilter.toLowerCase() + '/');
      });
    }

    if (term) {
      data = data.filter(
        (it) =>
          (it.fileName || '').toLowerCase().includes(term) ||
          (it.storagePath || '').toLowerCase().includes(term)
      );
    }

    const arr = [...data].sort((a, b) => {
      const aName = (a.fileName || '').toLowerCase();
      const bName = (b.fileName || '').toLowerCase();
      const aSize = a.bytes || 0;
      const bSize = b.bytes || 0;
      const aDate = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
      const bDate = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;

      if (sortBy === 'name')        return aName.localeCompare(bName);
      if (sortBy === 'size')        return aSize - bSize;
      if (sortBy === 'type')        return extOf(a.fileName).localeCompare(extOf(b.fileName));
      if (sortBy === 'meetingDate') {
        const am = readRowMeetingDate(a).sortKey;
        const bm = readRowMeetingDate(b).sortKey;
        return am.localeCompare(bm);
      }
      // default: 'date'
      return aDate - bDate;
    });
    return order === 'desc' ? arr.reverse() : arr;
  }, [items, search, sortBy, order, yearFilter, isTrash, currentCategory]);

  const clearSelection = () => setSelected(new Set());
  const toggleOne = (row) => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(row.id)) s.delete(row.id);
      else s.add(row.id);
      return s;
    });
  };
  const toggleAllVisible = () => {
    setSelected((prev) => {
      if (prev.size === filtered.length) return new Set();
      return new Set(filtered.map((r) => r.id));
    });
  };

  // ---------- DOWNLOAD CON FALLBACK DISCOVERY ----------
  const handleSingleDownload = async (row) => {
    const attempted = [];
    try {
      const candidates = buildCandidatePaths(row, yearFilter);
      let ok = false;

      for (const p of candidates) {
        attempted.push(p);
        try {
          ok = await tryDownloadFromPath(storage, p, row.fileName);
          if (ok) return; // finito
        } catch {
          // continua
        }
      }

      // Se non trovato: discovery via listAll
      const baseName = (row.fileName || '').split('/').pop() || row.fileName || '';
      const discoveredPath = await discoverPathByListing(storage, baseName);

      if (discoveredPath) {
        // scarica e aggiorna Firestore con storagePath corretto
        try {
          const blob = await getBlob(ref(storage, discoveredPath));
          triggerAnchorDownload(blob, row.fileName, discoveredPath);
        } catch {
          const url = await getDownloadURL(ref(storage, discoveredPath));
          const blob = await fetchBlob(url);
          triggerAnchorDownload(blob, row.fileName, discoveredPath);
        }

        if (row.id) {
          try {
            await updateDoc(doc(db, 'reports', row.id), { storagePath: discoveredPath });
          } catch {
            // non bloccare il download per un errore di update
          }
        }

        notify('success', `Trovato via scansione: ${discoveredPath}`);
        return;
      }

      const msg = `Download non riuscito • path provati: ${attempted.join(' | ')} • nessun match in discovery`;
      console.error('DOWNLOAD ERROR →', msg);
      notify('error', msg);
    } catch (err) {
      const msg = `Download non riuscito • ${err?.code || err?.message || 'errore sconosciuto'} • path provati: ${attempted.join(' | ')}`;
      console.error('DOWNLOAD ERROR →', msg, err);
      notify('error', msg);
    }
  };

  const handleSingleDelete = async (row) => {
    try {
      if (!isTrash) {
        const confirmed = await askConfirm({
          title: 'Sposta nel cestino',
          message: `Spostare \"${row.fileName}\" nel cestino?`,
          proceedLabel: 'Sposta',
          cancelLabel: 'Annulla',
        });
        if (!confirmed) return;
        const userEmail = (getAuth().currentUser && getAuth().currentUser.email) || 'sconosciuto';
        await updateDoc(doc(db, 'reports', row.id), {
          trashed: true,
          trashedAt: new Date(),
          deletedBy: userEmail,
          previousPath: row.storagePath || row.fileName || '',
        });
        notify('success', 'Spostato nel cestino.');
      } else {
        const confirmed = await askConfirm({
          title: 'Elimina definitivamente',
          message: `Eliminare definitivamente \"${row.fileName}\"?`,
          proceedLabel: 'Elimina',
          cancelLabel: 'Annulla',
        });
        if (!confirmed) return;
        if (row.storagePath) await deleteObject(ref(storage, row.storagePath)).catch(() => {});
        else await deleteObject(ref(storage, row.fileName)).catch(() => {});
        await deleteDoc(doc(db, 'reports', row.id));
        notify('success', 'Eliminato definitivamente.');
      }
    } catch (e) {
      console.error(e);
      notify('error', 'Operazione non riuscita.');
    }
  };

  const handleSingleRestore = async (row) => {
    try {
      const confirmed = await askConfirm({
        title: 'Ripristina file',
        message: `Ripristinare \"${row.fileName}\" dal cestino?`,
        proceedLabel: 'Ripristina',
        cancelLabel: 'Annulla',
      });
      if (!confirmed) return;
      await updateDoc(doc(db, 'reports', row.id), { trashed: false, trashedAt: null });
      notify('success', 'Ripristinato.');
    } catch (e) {
      console.error(e);
      notify('error', 'Ripristino non riuscito.');
    }
  };

  // ---------- BULK ----------
  const handleBulkDelete = async () => {
    if (selected.size === 0) {
      notify('info', 'Seleziona almeno un file.');
      return;
    }
    if (!isTrash) {
      const confirmed = await askConfirm({
        title: 'Sposta nel cestino',
        message: `Spostare ${selected.size} file nel cestino?`,
        proceedLabel: 'Sposta',
        cancelLabel: 'Annulla',
      });
      if (!confirmed) return;
      const userEmail = (getAuth().currentUser && getAuth().currentUser.email) || 'sconosciuto';
      let okCount = 0, koCount = 0;
      for (const id of selected) {
        const row = items.find((r) => r.id === id);
        if (!row) continue;
        try {
          await updateDoc(doc(db, 'reports', row.id), {
            trashed: true,
            trashedAt: new Date(),
            deletedBy: userEmail,
            previousPath: row.storagePath || row.fileName || '',
          });
          okCount++;
        } catch (e) {
          console.error(e);
          koCount++;
        }
      }
      notify(koCount ? 'warning' : 'success', koCount ? `Spostati nel cestino: ${okCount}, errori: ${koCount}` : `Spostati nel cestino: ${okCount}`);
      clearSelection();
    } else {
      const confirmed = await askConfirm({
        title: 'Elimina definitivamente',
        message: `Eliminare definitivamente ${selected.size} file dal cestino?`,
        proceedLabel: 'Elimina',
        cancelLabel: 'Annulla',
      });
      if (!confirmed) return;
      let okCount = 0, koCount = 0;
      for (const id of selected) {
        const row = items.find((r) => r.id === id);
        if (!row) continue;
        try {
          if (row.storagePath) await deleteObject(ref(storage, row.storagePath)).catch(() => {});
          else await deleteObject(ref(storage, row.fileName)).catch(() => {});
          await deleteDoc(doc(db, 'reports', row.id));
          okCount++;
        } catch (e) {
          console.error(e);
          koCount++;
        }
      }
      notify(koCount ? 'warning' : 'success', koCount ? `Eliminati: ${okCount}, errori: ${koCount}` : `Eliminati: ${okCount}`);
      clearSelection();
    }
  };

  // Sposta selezionati
  const moveSelectedToFolder = async (targetFolder) => {
    if (!targetFolder || targetFolder === 'all') {
      notify('warning', 'Seleziona una cartella anno valida.');
      return;
    }
    const toMove = Array.from(selected).map((id) => items.find((r) => r.id === id)).filter(Boolean);
    if (!toMove.length) {
      notify('info', 'Seleziona almeno un file.');
      return;
    }

    const confirmed = await askConfirm({
      title: 'Sposta selezionati',
      message: `Spostare ${toMove.length} file in \"${targetFolder}\"?`,
      proceedLabel: 'Sposta',
      cancelLabel: 'Annulla',
    });
    if (!confirmed) return;

    let okCount = 0, koCount = 0;

    for (const row of toMove) {
      try {
        const oldPathGuess = row.storagePath || row.fileName || row.downloadURL;
        const baseName = (row.fileName || (typeof oldPathGuess === 'string' ? oldPathGuess.split('/').pop() : '') || '').split('?')[0];
        if (!baseName) { okCount++; continue; }

        const newPath = `${targetFolder}/${baseName}`;
        if (oldPathGuess === newPath) { okCount++; continue; }

        let blob = null;

        if (isHttpUrl(oldPathGuess)) {
          blob = await fetchBlob(oldPathGuess);
        } else {
          try {
            blob = await getBlob(ref(storage, oldPathGuess));
          } catch {
            try {
              blob = await getBlob(ref(storage, baseName));
            } catch {
              const url = await getDownloadURL(ref(storage, oldPathGuess));
              blob = await fetchBlob(url);
            }
          }
        }

        const newRef = ref(storage, newPath);
        await uploadBytes(newRef, blob);

        if (!isHttpUrl(oldPathGuess)) {
          await deleteObject(ref(storage, oldPathGuess)).catch(() => {});
          if (oldPathGuess !== baseName) await deleteObject(ref(storage, baseName)).catch(() => {});
        }

        await updateDoc(doc(db, 'reports', row.id), { storagePath: newPath });

        okCount++;
      } catch (e) {
        console.error('moveSelectedToFolder error:', e);
        koCount++;
      }
    }

    notify(koCount ? 'warning' : 'success',
      koCount ? `Spostati: ${okCount}, errori: ${koCount}` : `Spostati: ${okCount}`
    );
    clearSelection();
  };

  // ZIP locale
  const handleBulkZipDownload = async () => {
    try {
      const toZipIds = Array.from(selected || []);
      if (!toZipIds.length) {
        notify('info', 'Seleziona almeno un file da includere nello ZIP.');
        return;
      }
      setZipping(true);
      setZipProgress(0);

      const { default: JSZip } = await import('jszip');
      const zip = new JSZip();

      let processed = 0;
      const total = toZipIds.length;

      for (const id of toZipIds) {
        const row = items.find((r) => r.id === id);
        if (!row) continue;

        const primaryPath = row.storagePath || row.fileName || row.downloadURL;
        if (!primaryPath) continue;

        try {
          let blob;
          if (isHttpUrl(primaryPath)) {
            blob = await fetchBlob(primaryPath);
          } else {
            try {
              blob = await getBlob(ref(storage, primaryPath));
            } catch {
              const url = await getDownloadURL(ref(storage, primaryPath));
              blob = await fetchBlob(url);
            }
          }

          const nameInZip = (row.fileName && row.fileName.split('/').pop())
            || (typeof primaryPath === 'string' ? primaryPath.split('/').pop() : '')
            || `file_${processed + 1}`;
          zip.file(nameInZip.split('?')[0], blob);
        } catch (e) {
          console.error('Errore lettura blob per ZIP:', primaryPath, e);
        } finally {
          processed += 1;
          setZipProgress(Math.round((processed / total) * 100));
        }
      }

      const content = await zip.generateAsync({ type: 'blob' }, (meta) => {
        if (typeof meta.percent === 'number') setZipProgress(Math.round(meta.percent));
      });

      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      const zipName = `reports_${stamp}.zip`;

      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = zipName;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 0);

      notify('success', 'ZIP pronto al download.');
    } catch (err) {
      console.error('handleBulkZipDownload error:', err);
      notify('error', 'Preparazione ZIP non riuscita.');
    } finally {
      setTimeout(() => setZipping(false), 200);
      setTimeout(() => setZipProgress(0), 300);
    }
  };

// === ZIP INTERA CARTELLA ANNO (preserva struttura) ===
async function listAllRecursive(folderRef, basePrefix = '') {
  // Ritorna lista di tuple (pathRelativo, storageRef)
  const { listAll } = await import('firebase/storage');
  const out = [];
  const stack = [{ ref: folderRef, prefix: basePrefix }];
  while (stack.length) {
    const { ref: cur, prefix } = stack.pop();
    const listing = await listAll(cur);
    for (const it of listing.items) {
      out.push([ prefix ? `${prefix}/${it.name}` : it.name, it ]);
    }
    for (const sub of listing.prefixes) {
      const name = sub.name || sub._location?.path_.split('/').pop() || '';
      const nextPrefix = prefix ? `${prefix}/${name}` : name;
      stack.push({ ref: sub, prefix: nextPrefix });
    }
  }
  return out;
}

const handleZipCurrentYear = async () => {
  try {
    if (isTrash) {
      notify('info', 'Operazione non disponibile nel Cestino.');
      return;
    }
    if (!yearFilter || yearFilter === 'all') {
      notify('warning', 'Seleziona prima una cartella/anno dalla lista.');
      return;
    }
    setZipping(true);
    setZipProgress(0);

    const { default: JSZip } = await import('jszip');
    const zip = new JSZip();

    const yearRef = ref(storage, yearFilter);
    const entries = await listAllRecursive(yearRef);

    if (!entries.length) {
      notify('info', `Nessun file trovato nella cartella "${yearFilter}".`);
      setZipping(false);
      return;
    }

    let processed = 0
    const total = entries.length

    for (const [relPath, storageRef] of entries) {
      try {
        let blob;
        try {
          blob = await getBlob(storageRef);
        } catch {
          const url = await getDownloadURL(storageRef);
          blob = await fetchBlob(url);
        }
        // Mantiene la struttura, prefissando il nome cartella anno
        zip.file(`${yearFilter}/${relPath}`, blob);
      } catch (e) {
        console.error('Errore lettura per ZIP anno:', relPath, e);
      } finally {
        processed += 1;
        setZipProgress(Math.round((processed / total) * 100));
      }
    }

    const content = await zip.generateAsync({ type: 'blob' }, (meta) => {
      if (typeof meta.percent === 'number') setZipProgress(Math.round(meta.percent));
    });

    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    const zipName = `${yearFilter.replace(/\//g,'_')}_${stamp}.zip`;

    const url = URL.createObjectURL(content);
    const a = document.createElement('a');
    a.href = url;
    a.download = zipName;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }, 0);

    notify('success', `ZIP della cartella "${yearFilter}" pronto al download.`);
  } catch (err) {
    console.error('handleZipCurrentYear error:', err);
    notify('error', 'Preparazione ZIP cartella anno non riuscita.');
  } finally {
    setTimeout(() => setZipping(false), 200);
    setTimeout(() => setZipProgress(0), 300);
  }
};

  // === ZIP TUTTE LE CARTELLE ANNO (multilivello) ===
  const handleZipAllYears = async () => {
    try {
      if (isTrash) {
        notify('info', 'Operazione non disponibile nel Cestino.');
        return;
      }

      setZipping(true);
      setZipProgress(0);

      const { default: JSZip } = await import('jszip');
      const { listAll } = await import('firebase/storage');
      const zip = new JSZip();

      // Livello root: recupera tutte le "cartelle anno"
      const rootRef = ref(storage, '');
      const rootList = await listAll(rootRef);
      const yearPrefixes = rootList.prefixes || [];

      // Filtra eventuali cartelle non anno (es. Trash) mantenendo però "Privacy" se la usi per i PDF privacy
      const isYearFolder = (name) => /^\d{4}($|\/)/.test(name);
      const folders = yearPrefixes.filter(pref => {
        const n = pref.name || pref._location?.path_.split('/').pop() || '';
        return isYearFolder(n) || n.toLowerCase() === 'privacy';
      });

      if (!folders.length) {
        notify('info', 'Nessuna cartella anno trovata allo storage.');
        setZipping(false);
        return;
      }

      // Totale files per progress
      let total = 0;
      const allEntries = [];
      for (const f of folders) {
        const baseName = f.name || f._location?.path_.split('/').pop() || '';
        const entries = await listAllRecursive(f, baseName);
        allEntries.push(...entries);
        total += entries.length;
      }

      if (!total) {
        notify('info', 'Cartelle anno vuote: nessun file da scaricare.');
        setZipping(false);
        return;
      }

      let processed = 0;
      for (const [relPath, storageRef] of allEntries) {
        try {
          let blob;
          try {
            blob = await getBlob(storageRef);
          } catch {
            const url = await getDownloadURL(storageRef);
            blob = await fetchBlob(url);
          }
          // relPath include già il prefisso dell'anno (baseName) inserito da listAllRecursive
          zip.file(relPath, blob);
        } catch (e) {
          console.error('Errore lettura per ZIP all years:', relPath, e);
        } finally {
          processed += 1;
          setZipProgress(Math.round((processed / total) * 100));
        }
      }

      const content = await zip.generateAsync({ type: 'blob' }, (meta) => {
        if (typeof meta.percent === 'number') setZipProgress(Math.round(meta.percent));
      });

      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      const zipName = `ALL_YEARS_${stamp}.zip`;

      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = zipName;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 0);

      notify('success', 'ZIP multilivello di tutte le cartelle anno pronto al download.');
    } catch (err) {
      console.error('handleZipAllYears error:', err);
      notify('error', 'Preparazione ZIP per tutte le cartelle anno non riuscita.');
    } finally {
      setTimeout(() => setZipping(false), 200);
      setTimeout(() => setZipProgress(0), 300);
    }
  };

  // === ZIP singola cartella ANNO per CATEGORIA corrente (bottone sul riquadro) ===
  const handleZipYearCategory = async (yearValue) => {
    try {
      if (isTrash) {
        notify('info', 'Operazione non disponibile nel Cestino.');
        return;
      }
      if (!yearValue || yearValue === 'all') {
        notify('warning', 'Cartella anno non valida.');
        return;
      }
      setZipping(true);
      setZipProgress(0);

      const { default: JSZip } = await import('jszip');
      const zip = new JSZip();

      // Sottocartelle rilevanti per la categoria attiva; se non definite, includi root anno.
      const subs = CAT_FOLDERS[currentCategory] || [''];
      const entriesAll = [];
      for (const sub of subs.length ? subs : ['']) {
        const baseRef = ref(storage, sub ? `${yearValue}/${sub}` : `${yearValue}`);
        const prefix = sub ? `${yearValue}/${sub}` : `${yearValue}`;
        try {
          const entries = await listAllRecursive(baseRef, prefix);
          entriesAll.push(...entries);
        } catch {
          // ignora cartella mancante
        }
      }

      if (!entriesAll.length) {
        // fallback: prova a includere l'intera cartella anno
        try {
          const fallbackEntries = await listAllRecursive(ref(storage, yearValue), yearValue);
          entriesAll.push(...fallbackEntries);
        } catch {
          // ignora eventuali errori
        }
      }

      if (!entriesAll.length) {
        notify('info', `Nessun file trovato in "${yearValue}" per la categoria selezionata.`);
        setZipping(false);
        return;
      }

      let processed = 0;
      const total = entriesAll.length;

      for (const [relPath, storageRef] of entriesAll) {
        try {
          let blob;
          try {
            blob = await getBlob(storageRef);
          } catch {
            const url = await getDownloadURL(storageRef);
            blob = await fetchBlob(url);
          }
          zip.file(relPath, blob);
        } catch (e) {
          console.error('Errore lettura per ZIP anno/categoria:', relPath, e);
        } finally {
          processed += 1;
          setZipProgress(Math.round((processed / total) * 100));
        }
      }

      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      const zipName = `${yearValue}_${currentCategory}_${stamp}.zip`;

      const content = await zip.generateAsync({ type: 'blob' }, (meta) => {
        if (typeof meta.percent === 'number') setZipProgress(Math.round(meta.percent));
      });

      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = zipName;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 0);

      notify('success', `ZIP di "${yearValue}" per "${currentCategory}" pronto al download.`);
    } catch (err) {
      console.error('handleZipYearCategory error:', err);
      notify('error', 'Preparazione ZIP cartella anno per categoria non riuscita.');
    } finally {
      setTimeout(() => setZipping(false), 200);
      setTimeout(() => setZipProgress(0), 300);
    }
  };

  const handleEmptyTrashVisible = async () => {
    if (isTrash) {
      if (!filtered.length) {
        notify('info', 'Nessun elemento nel cestino.');
        return;
      }
      const confirmed = await askConfirm({
        title: 'Svuota cestino',
        message: `Eliminare definitivamente ${filtered.length} file visibili?`,
        proceedLabel: 'Svuota',
        cancelLabel: 'Annulla',
      });
      if (!confirmed) return;
      let okCount = 0, koCount = 0;
      for (const row of filtered) {
        try {
          if (row.storagePath) await deleteObject(ref(storage, row.storagePath)).catch(() => {});
          else await deleteObject(ref(storage, row.fileName)).catch(() => {});
          await deleteDoc(doc(db, 'reports', row.id));
          okCount++;
        } catch (e) {
          console.error(e);
          koCount++;
        }
      }
      notify(koCount ? 'warning' : 'success', koCount ? `Eliminati: ${okCount}, errori: ${koCount}` : `Eliminati: ${okCount}`);
    }
  };

  // ---------- UI ----------
  return (
    <>
      <AppBar position="static" sx={{ bgcolor: 'primary.main' }}>
        <Toolbar>
          <ArchiveIcon sx={{ mr: 1 }} />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Archivio Report
          </Typography>
          <Button onClick={() => navigate('/dashboard')} variant="contained" color="secondary">
            Dashboard
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" className="reports-container">
        <Paper sx={{ p: 2, mt: 2 }} className="auth-card">
          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12}>
              <Tabs value={tab} onChange={handleTabChange} aria-label="tabs reports" className="auth-tabs">
                <Tab label="Settimanali" />
                <Tab label="Mensili" />
                <Tab label="Statistiche" />
                <Tab label="Privacy" />
                <Tab label="Cestino" icon={<DeleteIcon sx={{ color: 'error.main' }} />} iconPosition="start" sx={{ ml: 'auto' }} />
              </Tabs>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'space-between' } }}>
                <TextField
                  size="small"
                  label="Cerca per nome…"
                  value={search}
                  onChange={handleSearchChange}
                  sx={{ width: { xs: '100%', sm: 280, md: 360 } }}
                />

                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="year-filter-label">Cartella/Anno</InputLabel>
                  <Select
                    labelId="year-filter-label"
                    value={yearFilter}
                    label="Cartella/Anno"
                    onChange={(e) => setYearFilter(e.target.value)}
                  >
                    {YEAR_FILTERS.map((f) => (
                      <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="sort-by-label">Ordina per</InputLabel>
                    <Select
                      labelId="sort-by-label"
                      value={sortBy}
                      label="Ordina per"
                      onChange={(e) => sortAndPersist(e.target.value, order)}
                    >
                      <MenuItem value="date">Data caricamento</MenuItem>
                      <MenuItem value="name">Nome</MenuItem>
                      <MenuItem value="size">Dimensione</MenuItem>
                      <MenuItem value="type">Tipo</MenuItem>
                      <MenuItem value="meetingDate">Data incontro</MenuItem>
                    </Select>
                  </FormControl>
                  <Button onClick={toggleOrder} size="small" variant="outlined">
                    {order === 'asc' ? 'Asc ↑' : 'Desc ↓'}
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={2} sx={{ mt: 1, mb: 1 }}>
          <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-start' } }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showFolders}
                  onChange={(e) => { setShowFolders(e.target.checked); localStorage.setItem('reports_show_folders', String(e.target.checked)); }}
                />
              }
              label={showFolders ? 'Nascondi cartelle' : 'Mostra cartelle'}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showFiles}
                  onChange={(e) => { setShowFiles(e.target.checked); localStorage.setItem('reports_show_files', String(e.target.checked)); }}
                />
              }
              label={showFiles ? 'Nascondi file' : 'Mostra file'}
            />
          </Grid>
        </Grid>

        {yearFilter === 'all' && !isTrash && showFolders && (
          <Paper sx={{ p: 2, mt: 1 }} className="auth-card">
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Cartelle</Typography>
            <Grid container spacing={2}>
              {YEAR_FILTERS.filter(f => f.value !== 'all').map(f => (
                <Grid item xs={6} sm={4} md={3} key={f.value}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      display:'flex',
                      alignItems:'center',
                      gap:1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      position: 'relative',
                      transition: 'box-shadow .2s ease, transform .05s ease',
                      '&:hover': { boxShadow: 3 },
                      '&:active': { transform: 'scale(0.99)' },
                    }}
                    onClick={() => setYearFilter(f.value)}
                  >
                    {/* Bottone ZIP cartella anno per categoria corrente */}
                    <Tooltip title={`Scarica ${f.label} (${currentCategory})`}>
                      <IconButton
                        size="small"
                        sx={{ position: 'absolute', top: 4, right: 4 }}
                        onClick={(e) => { e.stopPropagation(); handleZipYearCategory(f.value); }}
                      >
                        <FolderZipIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Badge badgeContent={folderCounts[f.value] || 0} color="primary" overlap="circular">
                      <FolderIcon sx={{ color: 'primary.main' }} />
                    </Badge>
                    <Box sx={{ display:'flex', flexDirection:'column' }}>
                      <Typography>{f.label}</Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        {folderCounts[f.value] || 0} file
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {yearFilter !== 'all' && !isTrash && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <Button variant="text" onClick={() => setYearFilter('all')}>← Indietro</Button>
            <Typography variant="body2">
              Tutte le cartelle ▸ <strong>{(YEAR_FILTERS.find(f => f.value === yearFilter)?.label) || yearFilter}</strong>
            </Typography>
          </Box>
        )}

        {showFiles && (
          <Paper sx={{ p: 2, mt: 1, overflowX: 'auto' }} className="auth-card">
            <Grid container spacing={1} alignItems="stretch" sx={{ mb: 1 }} className="reports-toolbar">
              {isTrash && (
                <Grid item>
                  <Tooltip title="Svuota cestino (elementi visibili)">
                    <IconButton onClick={handleEmptyTrashVisible}>
                      <DeleteForeverIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}

              <Grid item>
                <Tooltip title="Seleziona tutti visibili">
                  <IconButton onClick={toggleAllVisible}>
                    <SelectAllIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item>
                <Tooltip title="Deseleziona tutti">
                  <IconButton onClick={clearSelection}>
                    <DeselectIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item>
                <Tooltip title="Scarica ZIP (selezionati)">
                  <IconButton onClick={handleBulkZipDownload} disabled={zipping}>
                    <FolderZipIcon />
                  </IconButton>
                </Tooltip>
              </Grid>

              <Grid item>
                <Tooltip title="Scarica cartella anno (ZIP)">
                  <span>
                    <IconButton onClick={handleZipCurrentYear} disabled={zipping || isTrash || yearFilter === 'all'}>
                      <FolderZipIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Grid>

              <Grid item>
                <Tooltip title="Scarica TUTTI gli anni (ZIP)">
                  <span>
                    <IconButton onClick={handleZipAllYears} disabled={zipping || isTrash}>
                      <FolderZipIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Grid>

              <Grid item>
                <Tooltip title={isTrash ? 'Elimina definitivamente (selezionati)' : 'Sposta nel cestino (selezionati)'}>
                  <IconButton onClick={handleBulkDelete}>
                    {isTrash ? <DeleteForeverIcon /> : <DeleteIcon />}
                  </IconButton>
                </Tooltip>
              </Grid>

              {!isTrash && (
                <Grid item xs>
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="move-to-label">Sposta selezionati in</InputLabel>
                    <Select
                      labelId="move-to-label"
                      label="Sposta selezionati in"
                      value=""
                      onChange={(e) => moveSelectedToFolder(e.target.value)}
                      disabled={selected.size === 0}
                    >
                      <MenuItem value=""><em>Seleziona cartella…</em></MenuItem>
                      {YEAR_FILTERS.filter(f => f.value !== 'all').map(f => (
                        <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>

            {zipping && (
              <Box className="zip-progress">
                <LinearProgress variant="determinate" value={zipProgress} />
                <Typography variant="caption">
                  Preparazione ZIP… {zipProgress}%
                </Typography>
              </Box>
            )}

            <div className="reports-table-container">
              <Table size="small" aria-label="reports table">
                <TableHead>
                  <TableRow>
                    <TableCell className="th-checkbox" padding="checkbox" />
                    <TableCell sortDirection={sortBy === 'name' ? (order === 'asc' ? 'asc' : 'desc') : false}>
                      <TableSortLabel {...headerSortProps('name')}>Nome</TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={sortBy === 'type' ? (order === 'asc' ? 'asc' : 'desc') : false}>
                      <TableSortLabel {...headerSortProps('type')}>Tipo</TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={sortBy === 'size' ? (order === 'asc' ? 'asc' : 'desc') : false}>
                      <TableSortLabel {...headerSortProps('size')}>Dimensione</TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={sortBy === 'meetingDate' ? (order === 'asc' ? 'asc' : 'desc') : false}>
                      <TableSortLabel {...headerSortProps('meetingDate')}>Data&nbsp;incontro</TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={sortBy === 'date' ? (order === 'asc' ? 'asc' : 'desc') : false}>
                      <TableSortLabel {...headerSortProps('date')}>Data caricamento</TableSortLabel>
                    </TableCell>
                    {isTrash && <TableCell>Cancellato il</TableCell>}
                    {isTrash && <TableCell>Cancellato da</TableCell>}
                    <TableCell align="right">Azioni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((row) => {
                    const ext = extOf(row.fileName);
                    const checked = selected.has(row.id);
                    const { display: meetingDateDisplay } = readRowMeetingDate(row);
                    const showMeeting = currentCategory !== 'stats' ? (meetingDateDisplay || '-') : '-';

                    return (
                      <TableRow key={row.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={checked}
                            onChange={() => toggleOne(row)}
                          />
                        </TableCell>
                        <TableCell className="cell-name" sx={{ maxWidth: 420 }}>
                          <span
                            title={row.fileName}
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              wordBreak: 'break-word',
                              whiteSpace: 'normal',
                            }}
                          >
                            {row.fileName}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={ext.toUpperCase()} color={extColor(ext)} />
                        </TableCell>
                        <TableCell>{fmtBytes(row.bytes)}</TableCell>
                        <TableCell>{showMeeting}</TableCell>
                        <TableCell>
                          {row.createdAt?.toDate ? row.createdAt.toDate().toLocaleString() : '-'}
                        </TableCell>
                        {isTrash && (
                          <>
                            <TableCell>
                              {row.trashedAt?.toDate ? row.trashedAt.toDate().toLocaleString() : (row.trashedAt ? new Date(row.trashedAt).toLocaleString() : '-')}
                            </TableCell>
                            <TableCell>
                              {row.deletedBy || '—'}
                            </TableCell>
                          </>
                        )}
                        
                        <TableCell align="right" className="cell-actions">
                          {!isTrash ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                              <Tooltip title="Scarica">
                                <IconButton size="small" onClick={() => handleSingleDownload(row)}>
                                  <DownloadIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Sposta nel cestino">
                                <IconButton size="small" onClick={() => handleSingleDelete(row)}>
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                              <Tooltip title="Ripristina">
                                <IconButton size="small" onClick={() => handleSingleRestore(row)}>
                                  <RestoreFromTrashIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Elimina definitivamente">
                                <IconButton size="small" onClick={() => handleSingleDelete(row)}>
                                  <DeleteForeverIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </TableCell>

                      </TableRow>
                    );
                  })}

                  {!filtered.length && (
                    <TableRow>
                      <TableCell colSpan={isTrash ? 9 : 7}>
                        <Typography align="center" sx={{ py: 4 }} className="empty-cell">
                          Nessun file presente per questa categoria/cartella.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Paper>
        )}
      </Container>

      {/* Conferma operazione */}
      <Dialog open={confirmOpen} onClose={handleConfirmCancel} aria-labelledby="confirm-dialog-title">
        <DialogTitle id="confirm-dialog-title">{confirmTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmCancel}>{confirmCancelLabel}</Button>
          <Button onClick={handleConfirmProceed} variant="contained" color="error">
            {confirmProceedLabel}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={closeSnack} severity={snack.type} sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
